(() => {
  'use strict';

  const PUBLIC_ENABLED = true; // __LB_PUBLIC_ENABLED__
  const VERSION = '2026-07-17-v3';
  const PRIVATE_TEST_TOKEN = 'ladyboss-popup-20260717-sapphire';
  const FORMS = Object.freeze({
    desktop: 'SDDjsT',
    mobile: 'SG2jCj',
  });
  const OWN_FORM_IDS = new Set(Object.values(FORMS));
  const STORAGE_KEY = 'ladyboss:welcome-popup:v1';
  const LOCK_KEY = 'ladyboss:welcome-popup:lock:v1';
  const LOCK_TTL_MS = 20 * 60 * 1000;
  const PERMANENT_UNTIL = 8_640_000_000_000_000;
  const EXCLUDED_PATH = /^\/(?:cart|checkout|checkouts|account)(?:\/|$)/i;
  const OWNED_MEDIUM = /^(?:email|sms)$/i;
  const MOBILE_QUERY = '(max-width: 767px)';
  const RULES = Object.freeze({
    desktop: Object.freeze({ delayMs: 8_000, scrollPercent: 8, scrollCapPx: 120 }),
    mobile: Object.freeze({ delayMs: 6_000, scrollPercent: 5, scrollCapPx: 80 }),
  });
  const DISMISSAL_COOLDOWNS = Object.freeze([
    7 * 60 * 1000,
    30 * 24 * 60 * 60 * 1000,
    90 * 24 * 60 * 60 * 1000,
    PERMANENT_UNTIL,
  ]);

  const runtime = {
    activeFormId: null,
    cartConfirmedEmpty: false,
    elapsed: false,
    exitSignal: false,
    freshEngagement: false,
    opened: false,
    opening: false,
    scrollQualified: false,
    startedAt: Date.now(),
    lastScrollY: window.scrollY,
    mobilePeakY: window.scrollY,
    mobilePeakAt: performance.now(),
    retryCount: 0,
    tabId:
      typeof window.crypto?.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  };

  const query = new URLSearchParams(window.location.search);
  const previewMode = query.get('lb_popup_preview') === PRIVATE_TEST_TOKEN;
  const previewResetOnly = previewMode && query.get('lb_popup_reset_only') === '1';

  function defaultState() {
    return {
      completed: false,
      dismissals: 0,
      emailCaptured: false,
      lastCloseAt: 0,
      lastFormId: null,
      lastPath: null,
      permanent: false,
      suppressedUntil: 0,
      version: 1,
    };
  }

  function storageWorks() {
    try {
      const probe = `${STORAGE_KEY}:probe`;
      window.localStorage.setItem(probe, '1');
      window.localStorage.removeItem(probe);
      return true;
    } catch {
      return false;
    }
  }

  function readJson(key, fallback) {
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return fallback;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : fallback;
    } catch {
      return fallback;
    }
  }

  function readState() {
    return { ...defaultState(), ...readJson(STORAGE_KEY, {}) };
  }

  function writeState(next) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return true;
    } catch {
      return false;
    }
  }

  function currentDevice() {
    return window.matchMedia(MOBILE_QUERY).matches ? 'mobile' : 'desktop';
  }

  function currentFormId() {
    return FORMS[currentDevice()];
  }

  function isExcludedVisit() {
    if (EXCLUDED_PATH.test(window.location.pathname)) return true;
    const medium = new URLSearchParams(window.location.search).get('utm_medium');
    return Boolean(medium && OWNED_MEDIUM.test(medium.trim()));
  }

  function suppressionActive(state, now = Date.now()) {
    return state.completed || state.permanent || Number(state.suppressedUntil || 0) > now;
  }

  function acquireLock(formId) {
    const now = Date.now();
    const lock = readJson(LOCK_KEY, null);
    if (lock && lock.expiresAt > now && lock.owner !== runtime.tabId) return false;
    try {
      window.localStorage.setItem(
        LOCK_KEY,
        JSON.stringify({ formId, owner: runtime.tabId, expiresAt: now + LOCK_TTL_MS, version: VERSION }),
      );
      return true;
    } catch {
      return false;
    }
  }

  function releaseLock(formId) {
    const lock = readJson(LOCK_KEY, null);
    if (!lock || lock.owner === runtime.tabId || lock.expiresAt <= Date.now()) {
      try {
        window.localStorage.removeItem(LOCK_KEY);
      } catch {
        // Storage failures are handled by the fail-closed eligibility check.
      }
    }
  }

  function hasVisibleDialog() {
    return Array.from(document.querySelectorAll('[role="dialog"]')).some((element) => {
      const style = window.getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
    });
  }

  function klaviyoConfirmed() {
    if (window._klOnsite) return true;
    return Array.from(document.scripts).some((script) => /klaviyo|onsite/i.test(script.src || ''));
  }

  function normalTriggerReady() {
    return runtime.elapsed && runtime.scrollQualified;
  }

  function engagementReady(state) {
    if (Number(state.dismissals || 0) !== 1) return true;
    return runtime.freshEngagement || runtime.exitSignal;
  }

  function eligibilityReady(state) {
    return (
      !isExcludedVisit() &&
      !suppressionActive(state) &&
      runtime.cartConfirmedEmpty &&
      engagementReady(state) &&
      (runtime.exitSignal || normalTriggerReady())
    );
  }

  function queueRetry() {
    if (runtime.retryCount >= 20 || runtime.opened || runtime.opening) return;
    runtime.retryCount += 1;
    window.setTimeout(attemptOpen, 500);
  }

  function attemptOpen() {
    if (runtime.opened || runtime.opening) return;
    const state = readState();
    if (!eligibilityReady(state)) return;
    if (runtime.activeFormId || hasVisibleDialog()) return;
    if (!klaviyoConfirmed()) {
      queueRetry();
      return;
    }

    const formId = currentFormId();
    if (!acquireLock(formId)) return;
    runtime.opening = true;
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push([
      'openForm',
      formId,
      () => {
        runtime.opened = true;
        runtime.opening = false;
        runtime.activeFormId = formId;
      },
    ]);
    window.setTimeout(() => {
      if (!runtime.opened) {
        runtime.opening = false;
        releaseLock(formId);
      }
    }, 8_000);
  }

  function qualifyScroll() {
    const rule = RULES[currentDevice()];
    const scrollable = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    const threshold = Math.min(rule.scrollCapPx, scrollable * (rule.scrollPercent / 100));
    if (threshold > 0 && window.scrollY >= threshold) runtime.scrollQualified = true;
  }

  function handleScroll() {
    const now = performance.now();
    const y = window.scrollY;

    qualifyScroll();
    if (Math.abs(y - runtime.lastScrollY) >= 24) runtime.freshEngagement = true;

    if (currentDevice() === 'mobile') {
      if (y >= runtime.mobilePeakY || now - runtime.mobilePeakAt > 3_000) {
        runtime.mobilePeakY = y;
        runtime.mobilePeakAt = now;
      } else if (runtime.mobilePeakY >= 120 && runtime.mobilePeakY - y >= 100 && now - runtime.mobilePeakAt <= 2_000) {
        runtime.exitSignal = true;
        runtime.freshEngagement = true;
      }
    }

    runtime.lastScrollY = y;
    attemptOpen();
  }

  function handlePointerExit(event) {
    if (currentDevice() !== 'desktop') return;
    if (event.relatedTarget || event.toElement || event.clientY > 0) return;
    runtime.exitSignal = true;
    runtime.freshEngagement = true;
    attemptOpen();
  }

  function updateStateForSubmission(detail) {
    const state = readState();
    const metadata = detail.metaData || detail.metadata || {};
    const stepName = String(metadata.stepName || detail.stepName || '').toLowerCase();
    const emailSubmitted = Boolean(metadata.email) || stepName.includes('email');
    const phoneSubmitted = Boolean(metadata.phone_number) || /sms|mobile|phone/.test(stepName);

    if (emailSubmitted) state.emailCaptured = true;
    if (phoneSubmitted) {
      state.completed = true;
      state.permanent = true;
      state.suppressedUntil = PERMANENT_UNTIL;
    }
    state.lastFormId = detail.formId || detail.formID || currentFormId();
    writeState(state);
  }

  function updateStateForClose(formId) {
    const state = readState();
    if (!state.completed) {
      const dismissals = Math.min(4, Number(state.dismissals || 0) + 1);
      const cooldown = DISMISSAL_COOLDOWNS[dismissals - 1];
      state.dismissals = dismissals;
      state.lastCloseAt = Date.now();
      state.lastFormId = formId;
      state.permanent = dismissals >= 4;
      state.suppressedUntil = state.permanent ? PERMANENT_UNTIL : Date.now() + cooldown;
      state.lastPath = window.location.pathname;
      writeState(state);
    }
    runtime.activeFormId = null;
    runtime.opened = false;
    runtime.opening = false;
    releaseLock(formId);
  }

  function handleFormEvent(event) {
    const detail = event.detail || {};
    const formId = detail.formId || detail.formID;
    const type = detail.type;
    if (!formId) return;

    if (type === 'open') {
      runtime.activeFormId = formId;
      if (OWN_FORM_IDS.has(formId)) {
        runtime.opened = true;
        runtime.opening = false;
      }
      return;
    }

    if (type === 'close') {
      if (OWN_FORM_IDS.has(formId)) updateStateForClose(formId);
      else if (runtime.activeFormId === formId) runtime.activeFormId = null;
      return;
    }

    if (OWN_FORM_IDS.has(formId) && (type === 'stepSubmit' || type === 'submit')) {
      updateStateForSubmission(detail);
    }
  }

  async function confirmEmptyCart() {
    try {
      const response = await window.fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      const cart = await response.json();
      runtime.cartConfirmedEmpty = Number(cart.item_count || 0) === 0 && Number(cart.total_price || 0) === 0;
      attemptOpen();
    } catch {
      runtime.cartConfirmedEmpty = false;
    }
  }

  function initialize() {
    if ((!PUBLIC_ENABLED && !previewMode) || !storageWorks() || isExcludedVisit()) return;

    if (previewMode) {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
        window.localStorage.removeItem(LOCK_KEY);
      } catch {
        return;
      }
      if (previewResetOnly) return;
    }

    const state = readState();
    if (state.completed || state.permanent) return;
    if (Number(state.suppressedUntil || 0) > Date.now()) {
      const wait = Math.min(Number(state.suppressedUntil) - Date.now() + 50, 2_147_000_000);
      window.setTimeout(initialize, wait);
      return;
    }

    if (previewMode) {
      runtime.elapsed = true;
      runtime.scrollQualified = true;
      runtime.freshEngagement = true;
    }

    const previousPath = state.lastPath;
    if (!previewMode) {
      runtime.freshEngagement = Boolean(
        state.lastCloseAt && previousPath !== null && previousPath !== window.location.pathname,
      );
    }
    state.lastPath = window.location.pathname;
    if (!writeState(state)) return;

    window.addEventListener('klaviyoForms', handleFormEvent);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('pagehide', () => releaseLock(currentFormId()), { once: true });
    document.documentElement.addEventListener('mouseout', handlePointerExit);

    if (!previewMode) {
      const rule = RULES[currentDevice()];
      window.setTimeout(() => {
        runtime.elapsed = true;
        qualifyScroll();
        attemptOpen();
      }, rule.delayMs);
    }

    qualifyScroll();
    confirmEmptyCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
